import { intervalToDuration, type Duration } from 'date-fns';

export const SECONDS_IN = {
  minute: 60,
  hour: 3600,
  day: 3600 * 24,
  week: 3600 * 24 * 7,
  month: 3600 * 24 * 30,
  semester: 3600 * 24 * 180,
  year: 3600 * 24 * 365,
} as const;

const UNIT_LABELS: Record<keyof Duration, string> = {
  years: 'y',
  months: 'mo',
  weeks: 'w',
  days: 'd',
  hours: 'h',
  minutes: 'm',
  seconds: 's',
};

function getFormat(seconds: number): (keyof Duration)[] {
  switch (true) {
    case seconds < SECONDS_IN.minute:
      return ['seconds'];
    case seconds < SECONDS_IN.hour:
      return ['minutes', 'seconds'];
    case seconds < SECONDS_IN.day:
      return ['hours', 'minutes'];
    case seconds < SECONDS_IN.month:
      return ['days', 'hours'];
    case seconds < SECONDS_IN.semester:
      return ['months', 'days'];
    default:
      return ['years', 'months'];
  }
}

function formatValue(value: number | undefined, unit: keyof Duration) {
  if (!value) {
    return null;
  }

  if (unit === 'months') {
    return `${String(value)}${value > 1 ? 'mos' : 'mo'}`;
  }

  return `${String(value)}${UNIT_LABELS[unit]}`;
}

export function formatSeconds(seconds: number | null) {
  if (seconds === null) {
    return '\u2014';
  }

  const start = 0;
  const end = seconds * 1000; // convert to milliseconds

  const duration = intervalToDuration({ start, end });

  const unitsToShow = getFormat(seconds);

  const parts = unitsToShow
    .map((unit) => formatValue(duration[unit], unit))
    .filter(Boolean) // Remove nulls
    .join(' ');

  return parts || '0s';
}
